import Filter from 'components/commons/filter'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { API_EMPLOYEE_ANALYTICS } from 'services/apiEmployeeAnalytics'
import { useFetch } from 'services/useFetch'
import { deepClone, getHeadUnitText, getYears } from 'utils/utils'

const NINE_BOX_CATEGORIES = [
    {
        label: 'Falling Star',
        value: 'Falling Star',
    },
    {
        label: 'Aspiring Star',
        value: 'Aspiring Star',
    },
    {
        label: 'Bright Star',
        value: 'Bright Star',
    },
    {
        label: 'Emerging Star',
        value: 'Emerging Star',
    },
    {
        label: 'Promising Star',
        value: 'Promising Star',
    },
    {
        label: 'Super Star',
        value: 'Super Star',
    },
    {
        label: 'Rock Star',
        value: 'Rock Star',
    },
    {
        label: 'Rising Star',
        value: 'Rising Star',
    },
    {
        label: 'Mega Star',
        value: 'Mega Star',
    },
]

const EMPLOYEE_TYPE = [
    {
        label: 'Worker',
        value: 'worker',
    },
    {
        label: 'Non Worker',
        value: 'non-worker',
    },
]

const FILTER = {
    NINE_BOX_LABEL: 'nine_box_label',
    EMPLOYEE_TYPE: 'employee_type',
    YEAR: 'year',
    YEAR_RANGE: 'year_range',
    DEPARTMENT_NAMES: 'department_names',
    COMPANY: 'company',
    DEPARTMENT: 'department',
    DIVISION: 'division',
    SUB_DIVISION: 'sub-division',
}

const QUERY_PARAMS = {
    [FILTER.DEPARTMENT_NAMES]: 'department_names',
    [FILTER.COMPANY]: 'company_ids',
    [FILTER.DEPARTMENT]: 'department_ids',
    [FILTER.DIVISION]: 'division_ids',
    [FILTER.SUB_DIVISION]: 'sub_division_ids',
}

const INITIAL_DATA_FILTER = [
    {
        label: 'Year Range',
        type: 'year',
        key: FILTER.YEAR_RANGE,
        value: FILTER.YEAR_RANGE,
        start_year: {
            label: 'Dari Tahun',
            name: 'start_year',
            placeholder: 'Pilih Tahun Awal',
            isRequired: true,
        },
        end_year: {
            label: 'Sampai Tahun',
            name: 'end_year',
            placeholder: 'Pilih Tahun Akhir',
            isRequired: true,
        },
    },
    {
        label: 'Category',
        value: FILTER.NINE_BOX_LABEL,
        key: FILTER.NINE_BOX_LABEL,
        type: 'select',
        placeholder: 'Pilih category',
    },
    {
        label: 'Employee Type',
        value: FILTER.EMPLOYEE_TYPE,
        key: FILTER.EMPLOYEE_TYPE,
        type: 'select',
        placeholder: 'Pilih type',
    },
    { label: 'Year', value: FILTER.YEAR, key: FILTER.YEAR, type: 'select', placeholder: 'Pilih tahun' },
    { label: 'Department', value: FILTER.DEPARTMENT_NAMES, key: QUERY_PARAMS[FILTER.DEPARTMENT_NAMES] },
    { label: 'Company', value: FILTER.COMPANY, key: QUERY_PARAMS[FILTER.COMPANY] },
    { label: 'Departement', value: FILTER.DEPARTMENT, key: QUERY_PARAMS[FILTER.DEPARTMENT] },
    { label: 'Divisi', value: FILTER.DIVISION, key: QUERY_PARAMS[FILTER.DIVISION] },
    { label: 'Sub Divisi', value: FILTER.SUB_DIVISION, key: QUERY_PARAMS[FILTER.SUB_DIVISION] },
]

const JOB_UNIT = [FILTER.DEPARTMENT, FILTER.DIVISION, FILTER.SUB_DIVISION]

const INITIAL_OPTIONS = {
    [FILTER.NINE_BOX_LABEL]: NINE_BOX_CATEGORIES,
    [FILTER.EMPLOYEE_TYPE]: EMPLOYEE_TYPE,
    [FILTER.YEAR]: getYears(2026),
    [FILTER.DEPARTMENT_NAMES]: [],
    [FILTER.COMPANY]: [],
    [FILTER.DEPARTMENT]: [],
    [FILTER.DIVISION]: [],
    [FILTER.SUB_DIVISION]: [],
}

const INITIAL_FILTER = {
    nine_box_label: 'Falling Star',
    employee_type: null,
    year: moment().get('year'),
    start_year: moment().get('year') - 1,
    end_year: moment().get('year'),
    department_names: [],
    company_ids: [],
    department_ids: [],
    division_ids: [],
    sub_division_ids: [],
}

const AnalyticItemFilterContainer = ({ title, initFilter = {}, initOptions = {}, onSubmit = () => {} }) => {
    const initialDataFilter = INITIAL_DATA_FILTER?.filter((item) => {
        if (item.type === 'year') {
            return 'start_year' in initFilter && 'end_year' in initFilter
        }

        return Object.hasOwn(initFilter, item?.key)
    })
    const initialFilter = Object.fromEntries(Object.entries(INITIAL_FILTER).filter(([key]) => key in initFilter))
    const [show, setShow] = useState(false)
    const [activeFilter, setActiveFilter] = useState({})
    const [options, setOptions] = useState(INITIAL_OPTIONS)
    const [filter, setFilter] = useState(initialFilter)
    const [filterCount, setFilterCount] = useState()
    const { fetching: getJobUnit } = useFetch(API_EMPLOYEE_ANALYTICS.LIST_JOB_UNIT)
    const { fetching: getCompany } = useFetch(API_EMPLOYEE_ANALYTICS.COMPANY)
    const { fetching: getDepartmentHappinessIndex } = useFetch(API_EMPLOYEE_ANALYTICS.DEPARTMENT_HAPPINESS_INDEX)

    useEffect(() => {
        if (initFilter) {
            const newFilter = deepClone(initFilter)
            setFilter(newFilter)
            setActiveFilter(initialDataFilter[0])
            countFilterLength(newFilter)
        }

        const buildOptions = async () => {
            const isFirstKeyCompany = Object.keys(initFilter)[0] === 'company_ids'
            const newOptions = { ...initOptions }

            if (show && isFirstKeyCompany && !options?.company?.length) {
                const companies = await getCompany()
                newOptions.company = companies?.data
            }

            setOptions((prev) => ({ ...prev, ...newOptions }))
        }

        buildOptions()
    }, [show])

    const onSelectFilter = (item) => {
        if (JOB_UNIT.includes(item.value) && options[item.value].length === 0) {
            getJobUnit(
                {
                    company_ids: filter?.company_ids,
                    head_unit_ids: filter?.[QUERY_PARAMS[getHeadUnitText[item.value]]] || [],
                },
                item.value,
            ).then((res) => setOptions({ ...options, [item.value]: res.data }))
        }

        if (item.value === FILTER.COMPANY && options[item?.value].length === 0) {
            getCompany().then((res) => setOptions({ ...options, [FILTER.COMPANY]: res?.data }))
        }

        if (item.value === FILTER.DEPARTMENT_NAMES && options[item?.value].length === 0) {
            getDepartmentHappinessIndex().then((res) =>
                setOptions({
                    ...options,
                    [FILTER.DEPARTMENT_NAMES]: res?.data?.map((item) => ({ id: item?.name, name: item?.name })),
                }),
            )
        }

        setActiveFilter(item)
    }

    const onChangeOption = (item) => {
        const { name, value, checked } = item
        const arrChecked = filter?.[QUERY_PARAMS[name]] || []
        handleClearOptionsFilter(name)

        if (checked) {
            arrChecked.push(String(value))
        } else {
            const indexRemove = arrChecked.indexOf(String(value))
            arrChecked.splice(indexRemove, 1)
        }

        setFilter({ ...filter, [QUERY_PARAMS[name]]: arrChecked })
    }

    const handleClearOptionsFilter = (filterName) => {
        if (filterName === FILTER.COMPANY) {
            options[FILTER.DEPARTMENT] = []
            options[FILTER.DIVISION] = []
            options[FILTER.SUB_DIVISION] = []
            filter[[QUERY_PARAMS[FILTER.DEPARTMENT]]] = []
            if (initialFilter[[QUERY_PARAMS[FILTER.DIVISION]]]) filter[[QUERY_PARAMS[FILTER.DIVISION]]] = []
            if (initialFilter[[QUERY_PARAMS[FILTER.SUB_DIVISION]]]) filter[[QUERY_PARAMS[FILTER.SUB_DIVISION]]] = []
        } else if (filterName === FILTER.DEPARTMENT) {
            options[FILTER.DIVISION] = []
            options[FILTER.SUB_DIVISION] = []
            if (initialFilter[[QUERY_PARAMS[FILTER.DIVISION]]]) filter[[QUERY_PARAMS[FILTER.DIVISION]]] = []
            if (initialFilter[[QUERY_PARAMS[FILTER.SUB_DIVISION]]]) filter[[QUERY_PARAMS[FILTER.SUB_DIVISION]]] = []
        } else if (filterName === FILTER.DIVISION) {
            options[FILTER.SUB_DIVISION] = []
            filter[[QUERY_PARAMS[FILTER.SUB_DIVISION]]] = []
        }

        setOptions({ ...options })
    }

    const onChangeSelect = (item) => {
        const { name, value } = item

        let newFilter = { ...filter, [name]: value?.value }

        if (name === 'start_year' && filter.end_year && value?.value > filter.end_year) {
            newFilter.end_year = value?.value
        }

        if (name === 'end_year' && filter.start_year && value?.value < filter.start_year) return

        setFilter(newFilter)
    }

    const onSubmitFilter = () => {
        onSubmit(filter)
        setShow(false)
    }

    const countFilterLength = (filter) => {
        let totalLength = 0
        for (const key in filter) {
            if (filter.hasOwnProperty(key)) {
                if (Array.isArray(filter[key])) {
                    totalLength += filter[key].length
                } else if (filter[key]) {
                    totalLength += 1
                }
            }
        }

        setFilterCount(totalLength)
    }

    const props = {
        title,
        show,
        onShow: () => setShow(true),
        onClose: () => setShow(false),
        queryParams: QUERY_PARAMS,
        filterList: initialDataFilter,
        activeFilter,
        onSelectFilter,
        filter,
        filterCount,
        options,
        onChangeOption,
        onChangeSelect,
        onSubmitFilter,
        onClearFilter: () => setFilter(deepClone(initialFilter)),
    }

    return <Filter {...props} />
}

export default AnalyticItemFilterContainer
